// @flow

// Pure transform from the two API-contract responses (stages + counts) into the
// grouped view model the funnel renders.
//
// Rules this function exists to enforce (from the design brief and the fixture
// contracts):
//   - Render groups in their given order, then stages in order within a group.
//     The inversion (closest-to-done at the top) is already baked into the
//     `order` fields, so there is no reversal logic here.
//   - Match a count to a stage by `stageKey`, never by array position.
//   - Bar width is proportional to count against the widest *bar* stage, with a
//     minimum so small counts stay legible.
//   - A stage with `rendersAsBar` false is not a bar: it drops beneath the
//     funnel as a plain count row and is excluded from the width calculation.
//   - No compliance status name is referenced — every label and key is read
//     from the data, and the number of groups/stages is whatever arrives.

import type { FunnelModel } from './types';

// Minimum bar width, as a percentage of the widest bar, so a stage holding only
// a handful of certs still renders a legible bar with its count inside it.
export const MIN_BAR_WIDTH_PCT = 6;

const byOrder = (a: Object, b: Object): number => a.order - b.order;

export const buildFunnelModel = (
  stagesData: Object,
  countsData: Object,
): FunnelModel => {
  // Count lookup keyed by stageKey — matching is by key, never by position.
  const countByStageKey = new Map(
    (countsData.counts || []).map((entry: Object) => [
      entry.stageKey,
      entry.count,
    ]),
  );
  const countFor = (stageKey: string): number => {
    const value = countByStageKey.get(stageKey);
    return typeof value === 'number' ? value : 0;
  };

  const stages = [...(stagesData.stages || [])].sort(byOrder);

  // Only bar-rendering stages set the scale; the row-only stage is excluded.
  const barCounts = stages
    .filter((stage: Object) => stage.rendersAsBar)
    .map((stage: Object) => countFor(stage.key));
  const maxCount = barCounts.length ? Math.max(...barCounts) : 0;

  const widthPctFor = (count: number): number =>
    maxCount > 0
      ? Math.max(MIN_BAR_WIDTH_PCT, (count / maxCount) * 100)
      : MIN_BAR_WIDTH_PCT;

  const groups = [...(stagesData.groups || [])]
    .sort(byOrder)
    .map((group: Object) => ({
      key: group.key,
      label: group.label,
      stages: stages
        .filter(
          (stage: Object) => stage.rendersAsBar && stage.groupKey === group.key,
        )
        .map((stage: Object) => {
          const count = countFor(stage.key);
          return {
            key: stage.key,
            label: stage.label,
            count,
            widthPct: widthPctFor(count),
          };
        }),
    }))
    // Drop any group that ended up with no bars, so an empty tinted block never
    // renders. (All four current groups have bars; this is defensive.)
    .filter((group: Object) => group.stages.length > 0);

  const holdStages = stages
    .filter((stage: Object) => !stage.rendersAsBar)
    .map((stage: Object) => ({
      key: stage.key,
      label: stage.label,
      count: countFor(stage.key),
    }));

  // Total open certs across every stage (bars and the hold row). Summed from
  // the counts rather than trusting a single response field, so the empty
  // state ("no open certs at this property") is detected honestly even if the
  // response omits its total. Per the contract this equals totalOpenCerts.
  const totalCount = (countsData.counts || []).reduce(
    (sum: number, entry: Object) =>
      sum + (typeof entry.count === 'number' ? entry.count : 0),
    0,
  );

  return {
    groups,
    holdStages,
    // The live stages endpoint returns `defaultStageKey` (no underscore). The
    // `_`-prefixed form only exists as an annotation in the contract fixture
    // (stages.json), so reading it here left the default undefined and nothing
    // selected on load.
    defaultStageKey: stagesData.defaultStageKey,
    totalCount,
  };
};
